import { AuthContextProvider } from "@elit-hair/elit-tools";
import { ErrorState, TooltipProvider } from "@elit-hair/elit-ui";
import "@fontsource/open-sans";
import { ErrorBoundary } from "@sentry/react";
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { Toaster } from "sonner";

import { GuidedTour } from "@/components/guided-tour/guided-tour";
import { GuidedTourProvider } from "@/components/guided-tour/guided-tour-context";
import { Router } from "@/components/main-router";
import { ClockProvider } from "@/providers/clock-provider";
import { LanguageProvider } from "@/providers/general/language-provider";
import { ThemeProviderWrapper } from "@/providers/general/theme-provider-wrapper";
import { ClinicProvider } from "@/providers/hooks/use-clinic";
import { TopbarProvider } from "@/providers/hooks/use-topbar";
import { handleAuthSvcLogout } from "@/utils/auth-token.utils";

import { DevIndicator } from "./components/dev-indicator";
import { SuspenseLoadingScreen } from "./components/loading-screen";
import { SettingsProvider } from "./providers/general/settings-provider";
import "./styles/globals.css";
import "./styles/reactQuill.css";
import { QueryClientProvider } from "./utils/queryClient";
import "./utils/sentry";

ReactDOM.createRoot(document.getElementById("root")!).render(
    <React.StrictMode>
        <ErrorBoundary
            fallback={
                <div className="flex items-center justify-center h-full">
                    <ErrorState
                        title="Something went wrong. Please try again later."
                        message="We encountered an error while loading your data. Please try again."
                        onRetry={() => window.location.reload()}
                    />
                </div>
            }
        >
            <Suspense fallback={<SuspenseLoadingScreen />}>
                <QueryClientProvider>
                    <TopbarProvider>
                        <AuthContextProvider beforeLogout={handleAuthSvcLogout}>
                            <SettingsProvider>
                                <ClinicProvider>
                                    <ClockProvider>
                                        <ThemeProviderWrapper>
                                            <LanguageProvider>
                                                <TooltipProvider>
                                                    <GuidedTourProvider>
                                                        <Router />
                                                        <GuidedTour />
                                                        <Toaster />
                                                        <DevIndicator />
                                                    </GuidedTourProvider>
                                                </TooltipProvider>
                                            </LanguageProvider>
                                        </ThemeProviderWrapper>
                                    </ClockProvider>
                                </ClinicProvider>
                            </SettingsProvider>
                        </AuthContextProvider>
                    </TopbarProvider>
                </QueryClientProvider>
            </Suspense>
        </ErrorBoundary>
    </React.StrictMode>
);
